import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type { ReactElement } from 'react';
import * as React from 'react';

import { Text } from '@/components/Text';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-greenDark hover:bg-greenUltraDark text-white',
        error: 'bg-declined hover:bg-declinedUltraDark text-white',
        ghost: 'border border-greyLight hover:bg-greyDark_20 text-greyDark',
      },
      size: {
        default: 'px-6 py-3.5 sm:py-2 rounded-button',
        error: 'px-6 py-3.5 sm:py-2 rounded-button',
        icon: 'p-1.5 sm:p-1 rounded',
      },
      isOutline: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        size: 'default',
        isOutline: true,
        className:
          'bg-transparent hover:bg-greenDark border border-greenDark text-greenDark hover:text-white',
      },
      {
        variant: 'error',
        size: 'default',
        isOutline: true,
        className:
          'bg-transparent hover:bg-declined border border-declined text-declined hover:text-white',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      isOutline: false,
    },
  },
);

const textVariants = cva('', {
  variants: {
    variant: {
      default: 'text-white',
      error: 'text-white',
      ghost: 'text-greyDark',
    },
    isOutline: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      isOutline: true,
      className: 'text-inherit',
    },
    {
      variant: 'error',
      isOutline: true,
      className: 'text-inherit',
    },
  ],
  defaultVariants: {
    variant: 'default',
    isOutline: false,
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  icon?: ReactElement;
  iconPosition?: 'left' | 'right';
  classNames?: {
    text?: string;
    loadingIcon?: string;
  };
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      isOutline = false,
      isLoading = false,
      disabled,
      icon,
      iconPosition = 'left',
      classNames,
      children,
      ...props
    },
    ref,
  ) => {
    const renderIcon = () => {
      let orderClass;

      if (icon || isLoading) {
        if (iconPosition === 'left') {
          orderClass = 'order-first';
        } else {
          orderClass = 'order-last';
        }
      }

      return (
        <div className={orderClass}>
          {isLoading ? (
            <Loader2
              className={cn(
                'animate-spin stroke-greenLight',
                classNames?.loadingIcon,
              )}
            />
          ) : (
            icon
          )}
        </div>
      );
    };

    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, isOutline, className }))}
        disabled={isLoading || disabled}
        {...props}
      >
        {(icon || isLoading) && renderIcon()}

        {children && (
          <Text
            renderAs='ctabold'
            className={cn(
              textVariants({
                variant,
                isOutline,
                className: classNames?.text,
              }),
            )}
          >
            {children}
          </Text>
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
